// Define the translation structure type
type TranslationStructure = {
  nav: {
    dashboard: string;
    students: string;
    subjects: string;
    sessions: string;
    settings: string;
    admin: string;
  };
  common: {
    save: string;
    cancel: string;
    delete: string;
    edit: string;
    create: string;
    add: string;
    back: string;
    next: string;
    previous: string;
    search: string;
    loading: string;
    error: string;
    success: string;
    confirm: string;
    name: string;
    actions: string;
    noData: string;
    required: string;
  };
  dashboard: {
    title: string;
    welcome: string;
    recentSessions: string;
    quickActions: string;
    newSession: string;
    addStudent: string;
    totalStudents: string;
    totalSessions: string;
    completedSessions: string;
  };
  students: {
    title: string;
    newStudent: string;
    editStudent: string;
    studentName: string;
    age: string;
    notes: string;
    skills: string;
    viewProfile: string;
    noStudents: string;
    deleteConfirm: string;
    skillOverview: string;
    proficiency: string;
    unlocked: string;
    locked: string;
  };
  subjects: {
    title: string;
    newSubject: string;
    subjectName: string;
    skills: string;
    noSubjects: string;
  };
  skills: {
    title: string;
    skill: string;
    prerequisites: string;
    difficulty: string;
    grade: string;
    proficiency: string;
    attempts: string;
    correct: string;
    history: string;
    noPrerequisites: string;
    unlockRequirement: string;
    manualChange: string;
    recentChanges: string;
  };
  sessions: {
    title: string;
    newSession: string;
    selectStudent: string;
    selectSubject: string;
    generate: string;
    generating: string;
    print: string;
    grade: string;
    complete: string;
    status: {
      draft: string;
      generated: string;
      graded: string;
      completed: string;
    };
    noSessions: string;
    exercises: string;
    problems: string;
  };
  exercises: {
    title: string;
    problem: string;
    answer: string;
    expectedAnswer: string;
    markCorrect: string;
    markIncorrect: string;
    markPartial: string;
    markSkipped: string;
    result: {
      pending: string;
      correct: string;
      incorrect: string;
      partial: string;
      skipped: string;
    };
  };
  grading: {
    title: string;
    submitGrades: string;
    updateProficiency: string;
    summary: string;
    correct: string;
    incorrect: string;
    total: string;
  };
  settings: {
    title: string;
    apiKey: string;
    apiKeyPlaceholder: string;
    apiKeyHelp: string;
    saved: string;
  };
  print: {
    worksheet: string;
    student: string;
    date: string;
    instructions: string;
  };
  errors: {
    generic: string;
    notFound: string;
    unauthorized: string;
    validation: string;
  };
};

export const translations: Record<string, TranslationStructure> = {
  lt: {
    nav: {
      dashboard: "Pradžia",
      students: "Mokiniai",
      subjects: "Dalykai",
      sessions: "Sesijos",
      settings: "Nustatymai",
      admin: "Administravimas",
    },
    common: {
      save: "Išsaugoti",
      cancel: "Atšaukti",
      delete: "Ištrinti",
      edit: "Redaguoti",
      create: "Sukurti",
      add: "Pridėti",
      back: "Grįžti",
      next: "Toliau",
      previous: "Atgal",
      search: "Ieškoti",
      loading: "Kraunama...",
      error: "Klaida",
      success: "Pavyko",
      confirm: "Patvirtinti",
      name: "Pavadinimas",
      actions: "Veiksmai",
      noData: "Nėra duomenų",
      required: "Privalomas laukas",
    },
    dashboard: {
      title: "Pradžia",
      welcome: "Sveiki atvykę į Scio",
      recentSessions: "Paskutinės sesijos",
      quickActions: "Greiti veiksmai",
      newSession: "Nauja sesija",
      addStudent: "Pridėti mokinį",
      totalStudents: "Iš viso mokinių",
      totalSessions: "Iš viso sesijų",
      completedSessions: "Užbaigtos sesijos",
    },
    students: {
      title: "Mokiniai",
      newStudent: "Naujas mokinys",
      editStudent: "Redaguoti mokinį",
      studentName: "Mokinio vardas",
      age: "Amžius",
      notes: "Pastabos",
      skills: "Įgūdžiai",
      viewProfile: "Peržiūrėti profilį",
      noStudents: "Nėra mokinių. Pridėkite pirmąjį mokinį!",
      deleteConfirm: "Ar tikrai norite ištrinti šį mokinį?",
      skillOverview: "Įgūdžių apžvalga",
      proficiency: "Mokėjimo lygis",
      unlocked: "Atrakinta",
      locked: "Užrakinta",
    },
    subjects: {
      title: "Dalykai",
      newSubject: "Naujas dalykas",
      subjectName: "Dalyko pavadinimas",
      skills: "Įgūdžiai",
      noSubjects: "Nėra dalykų.",
    },
    skills: {
      title: "Įgūdžiai",
      skill: "Įgūdis",
      prerequisites: "Reikalavimai",
      difficulty: "Sunkumas",
      grade: "Klasė",
      proficiency: "Mokėjimo lygis",
      attempts: "Bandymai",
      correct: "Teisingi",
      history: "Istorija",
      noPrerequisites: "Nėra reikalavimų",
      unlockRequirement: "Reikalingas ≥70% mokėjimo lygis",
      manualChange: "Rankinis pakeitimas",
      recentChanges: "Paskutiniai pokyčiai",
    },
    sessions: {
      title: "Sesijos",
      newSession: "Nauja sesija",
      selectStudent: "Pasirinkite mokinį",
      selectSubject: "Pasirinkite dalyką",
      generate: "Generuoti pratimus",
      generating: "Generuojama...",
      print: "Spausdinti",
      grade: "Vertinti",
      complete: "Užbaigti",
      status: {
        draft: "Juodraštis",
        generated: "Sugeneruota",
        graded: "Įvertinta",
        completed: "Užbaigta",
      },
      noSessions: "Nėra sesijų. Sukurkite pirmąją sesiją!",
      exercises: "Pratimai",
      problems: "Užduotys",
    },
    exercises: {
      title: "Pratimai",
      problem: "Užduotis",
      answer: "Atsakymas",
      expectedAnswer: "Laukiamas atsakymas",
      markCorrect: "Teisingai",
      markIncorrect: "Neteisingai",
      markPartial: "Iš dalies",
      markSkipped: "Praleista",
      result: {
        pending: "Laukiama",
        correct: "Teisingai",
        incorrect: "Neteisingai",
        partial: "Iš dalies",
        skipped: "Praleista",
      },
    },
    grading: {
      title: "Vertinimas",
      submitGrades: "Pateikti įvertinimus",
      updateProficiency: "Atnaujinti mokėjimo lygius",
      summary: "Rezultatų santrauka",
      correct: "Teisingai",
      incorrect: "Neteisingai",
      total: "Iš viso",
    },
    settings: {
      title: "Nustatymai",
      apiKey: "Gemini API raktas",
      apiKeyPlaceholder: "Įveskite savo Gemini API raktą",
      apiKeyHelp: "Gaukite API raktą iš Google AI Studio",
      saved: "Nustatymai išsaugoti",
    },
    print: {
      worksheet: "Darbo lapas",
      student: "Mokinys",
      date: "Data",
      instructions: "Instrukcijos",
    },
    errors: {
      generic: "Įvyko klaida. Bandykite dar kartą.",
      notFound: "Nerasta",
      unauthorized: "Neautorizuota",
      validation: "Neteisingi duomenys",
    },
  },

  en: {
    nav: {
      dashboard: "Dashboard",
      students: "Students",
      subjects: "Subjects",
      sessions: "Sessions",
      settings: "Settings",
      admin: "Admin",
    },
    common: {
      save: "Save",
      cancel: "Cancel",
      delete: "Delete",
      edit: "Edit",
      create: "Create",
      add: "Add",
      back: "Back",
      next: "Next",
      previous: "Previous",
      search: "Search",
      loading: "Loading...",
      error: "Error",
      success: "Success",
      confirm: "Confirm",
      name: "Name",
      actions: "Actions",
      noData: "No data",
      required: "Required field",
    },
    dashboard: {
      title: "Dashboard",
      welcome: "Welcome to Scio",
      recentSessions: "Recent Sessions",
      quickActions: "Quick Actions",
      newSession: "New Session",
      addStudent: "Add Student",
      totalStudents: "Total Students",
      totalSessions: "Total Sessions",
      completedSessions: "Completed Sessions",
    },
    students: {
      title: "Students",
      newStudent: "New Student",
      editStudent: "Edit Student",
      studentName: "Student Name",
      age: "Age",
      notes: "Notes",
      skills: "Skills",
      viewProfile: "View Profile",
      noStudents: "No students. Add your first student!",
      deleteConfirm: "Are you sure you want to delete this student?",
      skillOverview: "Skill Overview",
      proficiency: "Proficiency",
      unlocked: "Unlocked",
      locked: "Locked",
    },
    subjects: {
      title: "Subjects",
      newSubject: "New Subject",
      subjectName: "Subject Name",
      skills: "Skills",
      noSubjects: "No subjects.",
    },
    skills: {
      title: "Skills",
      skill: "Skill",
      prerequisites: "Prerequisites",
      difficulty: "Difficulty",
      grade: "Grade",
      proficiency: "Proficiency",
      attempts: "Attempts",
      correct: "Correct",
      history: "History",
      noPrerequisites: "No prerequisites",
      unlockRequirement: "Requires >=70% proficiency",
      manualChange: "Manual change",
      recentChanges: "Recent changes",
    },
    sessions: {
      title: "Sessions",
      newSession: "New Session",
      selectStudent: "Select Student",
      selectSubject: "Select Subject",
      generate: "Generate Exercises",
      generating: "Generating...",
      print: "Print",
      grade: "Grade",
      complete: "Complete",
      status: {
        draft: "Draft",
        generated: "Generated",
        graded: "Graded",
        completed: "Completed",
      },
      noSessions: "No sessions. Create your first session!",
      exercises: "Exercises",
      problems: "Problems",
    },
    exercises: {
      title: "Exercises",
      problem: "Problem",
      answer: "Answer",
      expectedAnswer: "Expected Answer",
      markCorrect: "Correct",
      markIncorrect: "Incorrect",
      markPartial: "Partial",
      markSkipped: "Skipped",
      result: {
        pending: "Pending",
        correct: "Correct",
        incorrect: "Incorrect",
        partial: "Partial",
        skipped: "Skipped",
      },
    },
    grading: {
      title: "Grading",
      submitGrades: "Submit Grades",
      updateProficiency: "Update Proficiencies",
      summary: "Results Summary",
      correct: "Correct",
      incorrect: "Incorrect",
      total: "Total",
    },
    settings: {
      title: "Settings",
      apiKey: "Gemini API Key",
      apiKeyPlaceholder: "Enter your Gemini API key",
      apiKeyHelp: "Get your API key from Google AI Studio",
      saved: "Settings saved",
    },
    print: {
      worksheet: "Worksheet",
      student: "Student",
      date: "Date",
      instructions: "Instructions",
    },
    errors: {
      generic: "An error occurred. Please try again.",
      notFound: "Not found",
      unauthorized: "Unauthorized",
      validation: "Invalid data",
    },
  },
};

export type Language = keyof typeof translations;
export type TranslationKeys = TranslationStructure;
